import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Dashboard as DashboardIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  MoneyOff as MoneyOffIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';

// Import services and components
import schoolFeesDataService from './services/schoolFeesDataService';
import SchoolFeesReport from './components/accounting/SchoolFeesReport';
import FinancialDashboard from './components/accounting/FinancialDashboard';
import IncomeStatementTab from './components/accounting/IncomeStatementTab';
import ExpensesTab from './components/accounting/ExpensesTab';
import AccountsReceivableTab from './components/accounting/AccountsReceivableTab';
import TrialBalance from './components/accounting/TrialBalance';
import ChartOfAccounts from './components/accounting/ChartOfAccounts';

const Accounting = () => {
  // Redux state
  const schooldetails = useSelector(state => state.schooldetails || []);
  const students = useSelector(state => state.students?.data || state.students || []);
  const classData = useSelector(state => state.class?.data || state.class || []);
  
  const currentSchool = schooldetails.length > 0 ? schooldetails[0] : null;
  
  // Local state
  const [activeTab, setActiveTab] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [schoolFeesData, setSchoolFeesData] = useState(null);

  // Refs for printing
  const schoolFeesRef = useRef();

  // Load school fees data
  const loadSchoolFeesData = async () => {
    if (!currentSchool?.schoolID) {
      setError('No school selected');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get mainterm (current term) - matching school fees page logic
      // mainterm = latest term from schooldetails (like tableschoolfees.js line 680)
      // If no terms exist, use "none" to match school fees page behavior
      const mainterm = currentSchool.terms && currentSchool.terms.length > 0
        ? currentSchool.terms[currentSchool.terms.length - 1]
        : "none";

      const data = await schoolFeesDataService.getSchoolFeesData(
        students,
        classData,
        startDate,
        endDate,
        mainterm  // Pass mainterm to match school fees page logic
      );

      setSchoolFeesData(data);
    } catch (error) {
      console.error('Error loading school fees data:', error);
      setError('Failed to load school fees data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount and when dates change
  useEffect(() => {
    loadSchoolFeesData();
  }, [currentSchool, startDate, endDate, students, classData]);

  // Handle period change
  const handlePeriodChange = (event) => {
    const period = event.target.value;
    setSelectedPeriod(period);
    
    const today = new Date();
    switch (period) {
      case 'current':
        setStartDate(new Date(today.getFullYear(), today.getMonth(), 1));
        setEndDate(today);
        break;
      case 'quarter':
        const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
        setStartDate(quarterStart);
        setEndDate(today);
        break;
      case 'year':
        setStartDate(new Date(today.getFullYear(), 0, 1));
        setEndDate(today);
        break;
      default:
        break;
    }
  };

  // Print handler
  const handlePrint = () => {
    if (schoolFeesRef.current) {
      const printContent = schoolFeesRef.current;
        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent.outerHTML);
        printWindow.document.close();
        printWindow.print();
      }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadSchoolFeesData}>
          Retry
        </Button>
      </Container>
    );
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Accounting Reports
        </Typography>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label="accounting reports tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab icon={<DashboardIcon />} label="Dashboard" iconPosition="start" />
            <Tab icon={<ReceiptIcon />} label="School Fees" iconPosition="start" />
            <Tab icon={<AccountBalanceIcon />} label="Income Statement" iconPosition="start" />
            <Tab icon={<MoneyOffIcon />} label="Expenses" iconPosition="start" />
            <Tab label="Accounts Receivable" />
            <Tab label="Cash Flow" />
            <Tab icon={<AccountBalanceIcon />} label="Trial Balance" iconPosition="start" />
            <Tab icon={<AccountBalanceIcon />} label="Chart of Accounts" iconPosition="start" />
          </Tabs>
        </Box>
      </Box>

      <Box sx={{ mt: 3 }}>
        {activeTab === 0 && (
          <FinancialDashboard />
        )}

        {activeTab === 1 && (
          <>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Period</InputLabel>
                <Select
                  value={selectedPeriod}
                  onChange={handlePeriodChange}
                >
                  <MenuItem value="current">Current Month</MenuItem>
                  <MenuItem value="quarter">Current Quarter</MenuItem>
                  <MenuItem value="year">Current Year</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
              
              {selectedPeriod === 'custom' && (
                <>
                  <TextField
                    label="Start Date"
                    type="date"
                    value={startDate.toISOString().split('T')[0]}
                    onChange={(e) => setStartDate(new Date(e.target.value))}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    label="End Date"
                    type="date"
                    value={endDate.toISOString().split('T')[0]}
                    onChange={(e) => setEndDate(new Date(e.target.value))}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </>
              )}
              
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadSchoolFeesData}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>

            <Paper sx={{ width: '100%' }}>
              <Box sx={{ p: 3 }}>
                <div ref={schoolFeesRef}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5">School Fees Report</Typography>
                    <Button
                      variant="outlined"
                      startIcon={<PrintIcon />}
                      onClick={handlePrint}
                    >
                      Print
                    </Button>
                  </Box>
                  <SchoolFeesReport 
                    data={schoolFeesData} 
                    formatCurrency={schoolFeesDataService.formatCurrency}
                  />
                </div>
              </Box>
            </Paper>
          </>
        )}

        {activeTab === 2 && (
          <IncomeStatementTab />
        )}

        {activeTab === 3 && (
          <ExpensesTab />
        )}

        {activeTab === 4 && (
          <AccountsReceivableTab />
        )}

        {activeTab === 5 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>Cash Flow Statement</Typography>
            <Alert severity="info">Cash Flow Statement component coming soon...</Alert>
          </Paper>
        )}

        {activeTab === 6 && (
          <TrialBalance />
        )}

        {activeTab === 7 && (
          <ChartOfAccounts />
        )}
      </Box>
    </Container>
  );
};

export default Accounting;
